import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, ArrowRight } from 'lucide-react';
import './PromptValidationModal.css';

interface PromptValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorMessage: string;
  suggestions?: string[];
  theme?: 'default' | 'playground';
}

const PromptValidationModal: React.FC<PromptValidationModalProps> = ({
  isOpen,
  onClose,
  errorMessage,
  suggestions = [],
  theme = 'default'
}) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Sync with system dark mode
  useEffect(() => {
    const checkTheme = () => {
      const savedTheme = localStorage.getItem("theme");
      setIsDarkMode(savedTheme === "dark");
    };

    checkTheme();
    window.addEventListener("storage", checkTheme);
    const interval = setInterval(checkTheme, 100);

    return () => {
      window.removeEventListener("storage", checkTheme);
      clearInterval(interval);
    };
  }, []);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalClass = `prompt-modal-v2 ${theme} ${isDarkMode ? 'dark' : 'light'}`;
  const overlayClass = `prompt-modal-v2-overlay ${theme} ${isDarkMode ? 'dark' : 'light'}`;

  return (
    <div className={overlayClass} onClick={onClose}>
      <div 
        className={modalClass} 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button 
          className="modal-v2-close"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* Warning Icon with animated border */}
        <div className="modal-v2-icon-container">
          <div className="modal-v2-icon-border"></div>
          <AlertTriangle className="modal-v2-icon" size={40} />
        </div>

        {/* Content */}
        <div className="modal-v2-content">
          <h2 className="modal-v2-title">Hmm, that doesn't look right</h2>
          <p className="modal-v2-message">{errorMessage}</p>

          {/* Suggestions */}
          {suggestions && suggestions.length > 0 && (
            <div className="modal-v2-suggestions">
              <h3 className="modal-v2-suggestions-title">Here are some ideas to get you started:</h3>
              <div className="modal-v2-suggestions-list">
                {suggestions.map((suggestion, index) => (
                  <div key={index} className="modal-v2-suggestion">
                    <ArrowRight className="suggestion-arrow" size={18} />
                    <span>{suggestion.replace(/^Try:\s*['"]/i, '').replace(/['"]$/, '')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action button */}
          <button 
            className="modal-v2-action-btn"
            onClick={onClose}
          >
            Got it, I'll try again
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptValidationModal;